/**
 * Import DNS — le contrat d'échange entre l'API et l'écran du rapport de
 * différences (RG-PA-05).
 *
 * Le dépôt lui-même est un envoi `multipart/form-data` : un fichier, et la
 * période déclarée. Zod ne le décrit pas ; la route le lit et le borne.
 */
import { z } from 'zod';
import { CHAMPS_RESOLUBLES } from '../dns/pivot.js';
import { CHAMPS_SAISIE } from '../dns/saisie.js';
import { MOTIFS_RETRAIT_AYANT_DROIT } from '../rules/ayants-droit.js';
import { CHAMPS_CORRIGEABLES } from '../dns/verification.js';
export const STATUTS_IMPORT_DNS = [
    'RECU',
    'EN_SAISIE',
    'EN_ANALYSE',
    'A_REPARTIR',
    'A_MAPPER',
    'ANALYSE',
    'VALIDE',
    'EN_APPLICATION',
    'APPLIQUE',
    'ANNULE',
    'REJETE',
    'ECHEC',
];
/** CP-11 — le support d'une déclaration : il décide du canal de traitement et de la preuve. */
export const SUPPORTS_DNS = ['EXCEL', 'PDF_NATIF', 'SCAN', 'PAPIER'];
/** Les supports qui passent par la grille de saisie de masse. */
export const SUPPORTS_SAISIE = [
    'PAPIER',
    'SCAN',
    'PDF_NATIF',
];
export const LIBELLE_SUPPORT_DNS = {
    EXCEL: 'Classeur Excel',
    PDF_NATIF: 'PDF',
    SCAN: 'Scan reçu par courriel',
    PAPIER: 'Papier, remis en main propre',
};
/**
 * Décision 227 — ce que déclare le fichier : la liste complète des salariés,
 * ou seulement les embauches et les départs. Choisi à l'envoi, jamais deviné.
 */
export const MODES_IMPORT_DNS = ['COMPLET', 'MOUVEMENTS'];
export const LIBELLE_MODE_DNS = {
    COMPLET: 'Liste complète',
    MOUVEMENTS: 'Seulement les embauches et départs',
};
/** Plafond d'un scan ou d'un PDF rattaché : plusieurs pages numérisées pèsent vite. */
export const TAILLE_MAX_SCAN_OCTETS = 20 * 1024 * 1024;
/**
 * Revue S98 (M1) — plafond de TOUTES les pages d'un même document papier,
 * envoi par envoi et cumulé. La lecture automatique s'arrête à 10 pages
 * (`PAGES_MAX_OCR`) ; 10 photos de la taille de la plus grosse reçue le 16/10
 * (4 Mo) font 40 Mo, et une page seule peut toujours peser 20 Mo.
 */
export const TAILLE_MAX_RECEPTION_OCTETS = 60 * 1024 * 1024;
/** Plafond d'un DNS déposé. Le plus gros classeur du corpus pèse moins de 2 Mo. */
export const TAILLE_MAX_DNS_OCTETS = 10 * 1024 * 1024;
const valeur = z.union([z.string(), z.number(), z.null()]);
/**
 * CP-03 — la couleur d'une ligne, montrée à la chargée pour qu'elle tranche :
 * la teinte telle que le fichier la porte, la colonne, et ce que la règle
 * propose (parti : la ligne entière en rouge ou en orange).
 */
const schemaIndiceCouleur = z.object({
    hexa: z.string().regex(/^[0-9A-F]{6}$/),
    champ: z.string().nullable(),
    libelle: z.string().nullable(),
    depart: z.boolean(),
    /**
     * Décision du TL (30/09) — `LIGNE` : toute la ligne, « parti » proposé ;
     * `IDENTITE` : le nom ou le matricule seulement, à vérifier ; `AUTRE` :
     * ailleurs, pour information. Absent des rapports calculés avant.
     */
    portee: z.enum(['LIGNE', 'IDENTITE', 'AUTRE']).optional(),
});
/**
 * CP-03 — une ligne colorée d'une personne à créer se tranche-t-elle ? Une
 * ligne entière ou une identité colorée, oui ; une couleur ailleurs, non
 * (décision du TL, 30/09). Un rapport calculé avant, sans portée, tranche
 * toute ligne colorée, comme alors.
 */
export function couleurATrancher(c) {
    return c !== undefined && c.portee !== 'AUTRE';
}
/** CP-03 (30/09) — le nom ou le matricule seulement : rien n'est proposé, la chargée vérifie. */
export function couleurAVerifier(c) {
    return c?.portee === 'IDENTITE';
}
/** Le motif d'un départ à vérifier : couleur sur le nom ou le matricule seulement (30/09). */
export const MOTIF_COULEUR_PARTIELLE = 'COULEUR_PARTIELLE';
/**
 * Décision du TL (30/09, second temps) : la ligne entière en rouge ou en
 * orange dit « parti », sans question. La personne à créer l'est inactive,
 * sauf si la chargée la remet « actif ».
 */
export function couleurPartiParDefaut(c) {
    return c?.portee === 'LIGNE';
}
/**
 * CP-03 (30/09) — la couleur exige une décision explicite : le nom ou le
 * matricule seul en couleur. Un rapport calculé avant, sans portée, en
 * exige une aussi, comme alors.
 */
export function couleurExigeDecision(c) {
    return c !== undefined && (c.portee === 'IDENTITE' || c.portee === undefined);
}
/**
 * Un salarié connu à la ligne entière colorée, ou dont le DNS donne une date
 * de départ passée (R10, décision du TL du 16/10) : « Parti » est pré-coché,
 * la chargée confirme en validant, et « garder actif » reste possible.
 */
export function departParDefaut(motif) {
    return motif === 'DEPART_DECLARE_COULEUR' || motif === 'DEPART_DECLARE_DATE';
}
/** Un salarié connu au nom ou au matricule seul coloré : son départ se décide, un par un. */
export function departExigeDecision(motif) {
    return motif === MOTIF_COULEUR_PARTIELLE;
}
/** Un ayant droit connu à la sous-ligne entière colorée : retiré sauf « garder actif ». */
export function retraitParDefaut(c) {
    return c.parti === true && c.aVerifier !== true && c.personneId != null;
}
/** Un ayant droit connu au nom seul coloré : son retrait se décide. */
export function retraitExigeDecision(c) {
    return c.parti === true && c.aVerifier === true && c.personneId != null;
}
const ligneRapport = z.object({
    personneId: z.uuid().nullable(),
    numeroLigne: z.number().int().nullable(),
    nomComplet: z.string(),
    matricule: z.string().nullable(),
});
/**
 * Ce qu'un nouveau salarié portera une fois créé, tel que lu — dates au
 * format AAAA-MM-JJ. Au premier import, c'est ce que la chargée vérifie
 * ligne à ligne (voir `dns/verification.ts`).
 */
const schemaDonneesNouveau = z.object({
    nom: z.string(),
    prenom: valeur,
    dateNaissance: valeur,
    sexe: valeur,
    fonction: valeur,
    lieuAffectation: valeur,
    dateEntree: valeur,
    dateDepart: valeur,
    nbConjoint: valeur,
    nbEnfant: valeur,
});
/**
 * Réunion du 15/10 (famille, phase 1) — une fiche qui désigne probablement la
 * même personne qu'une ligne du DNS : déjà en base, ou une autre ligne du
 * même fichier.
 */
const schemaFicheSemblable = z.object({
    /** `null` : une autre ligne de ce fichier, pas encore créée. */
    personneId: z.string().nullable(),
    nomComplet: z.string(),
    typeBeneficiaire: z.enum(['TITULAIRE', 'CONJOINT', 'ENFANT', 'EXTERNE']),
    /** Le salarié de rattachement, en clair ; `null` pour un salarié ou un externe. */
    titulaire: z.string().nullable(),
    /** Quand c'est une autre ligne de ce fichier. */
    numeroLigne: z.number().int().optional(),
});
export const schemaRapportDiff = z.object({
    nouveaux: z.array(ligneRapport.extend({
        inactif: z.boolean(),
        /** Absents des rapports calculés avant la vérification du premier import. */
        donnees: schemaDonneesNouveau.optional(),
        /** Les cellules à regarder : lecture incomplète, valeur invraisemblable, absence. */
        alertes: z.array(z.object({ champ: z.string(), motif: z.string() })).optional(),
        /** CP-03 — la ligne est colorée : la chargée tranche, parti ou actif. */
        couleur: schemaIndiceCouleur.optional(),
    })),
    modifies: z.array(ligneRapport.extend({
        changements: z.array(z.object({ champ: z.string(), avant: valeur, apres: valeur })),
    })),
    retires: z.array(ligneRapport),
    partis: z.array(ligneRapport.extend({ motif: z.string() })),
    /**
     * Réunion du 15/10 — des personnes inactives que le fichier cite de
     * nouveau, sans couleur ni date de départ : elles redeviennent actives à
     * l'application, sauf si la chargée décoche. Absente quand il n'y en a pas,
     * et dans les rapports calculés avant.
     */
    reactives: z
        .array(ligneRapport.extend({
        /**
         * Décision 3 du TL (16/10) : déclaré parti par la chargée. Il reste
         * parti sauf si elle coche la case.
         */
        aLaMain: z.literal(true).optional(),
    }))
        .optional(),
    aConfirmer: z.array(ligneRapport.extend({
        raison: z.enum(['SAISIE_POSTERIEURE', 'NON_CONFIRMEE', 'MATRICULE_REJETE']),
        /** N4 — la ligne rejetée `MATRICULE_D_UN_AUTRE` qui reprend son matricule. */
        ligneRejetee: z.number().int().optional(),
        /** Décision 194 — la ligne rejetée a une autre date de naissance ou un autre sexe. */
        autrePersonneProbable: z.literal(true).optional(),
    })),
    rejets: z.array(z.object({
        numeroLigne: z.number().int(),
        gravite: z.enum(['REJET', 'AVERTISSEMENT']),
        motif: z.string(),
        valeur: z.string(),
        /**
         * Une ligne répétée : celle qui porte la personne, plus haut ou sur une
         * feuille précédente. Absente des rapports calculés avant.
         */
        ligneOrigine: z.number().int().optional(),
    })),
    signalements: z.array(z.object({
        numeroLigne: z.number().int(),
        personneId: z.uuid(),
        motif: z.string(),
        detail: z.string(),
    })),
    effectifReference: z.number().int(),
    /** Décision 77 — le rapprochement a raté : les retraits sont refusés tant qu'il n'est pas vérifié. */
    rapprochementAVerifier: z
        .object({ nonRetrouves: z.number().int(), effectif: z.number().int() })
        .optional(),
    /**
     * CP-06 — le fichier parle-t-il bien de cette entreprise ? `null` quand la
     * question ne se pose pas : une grille de saisie n'a pas d'en-tête de
     * gabarit. Voir `dns/concordance.ts`.
     */
    concordance: z
        .object({
        statut: z.enum(['CONCORDE', 'DIVERGE', 'INCONNUE']),
        lu: z.object({
            raisonSociale: z.string().nullable(),
            nif: z.string().nullable(),
        }),
        detail: z.string().nullable(),
    })
        .nullable(),
    /**
     * RG-FIN-01 — ce que le fichier déclare de salaires, en agrégé : jamais un
     * salaire par personne dans le rapport. Retiré de la réponse pour qui n'a
     * pas `salaire:read` (voir `dns/routes.ts`). Absent des rapports
     * calculés avant l'ajout des salaires.
     */
    salaires: z
        .object({
        /** Lignes qui portent un salaire lisible. */
        declares: z.number().int().nonnegative(),
        /** Lignes sans salaire : colonne absente, vide, ou montant illisible. */
        sansSalaire: z.number().int().nonnegative(),
        illisibles: z.number().int().nonnegative(),
        /** Somme des salaires déclarés, ramenée au mois, en ariary. */
        masseMensuelle: z.number().nonnegative(),
        /** 3 pour une DNS trimestrielle : les montants couvrent trois mois. */
        moisCouverts: z.union([z.literal(1), z.literal(3)]),
        /** La colonne a été écartée en bloc : ses montants ne sont pas des salaires. */
        ecarte: z.object({ medianeMensuelle: z.number() }).nullable(),
        /** Décision 79 — le fichier ne donnait que le salaire de base, lu à la place du brut. */
        baseSeulement: z.literal(true).optional(),
    })
        .optional(),
    /**
     * Numéros des lignes lues comme de l'information et non comme des
     * personnes : récapitulatif sous le tableau, cotisations, légende,
     * signature. Montrés pour qu'une erreur de tri se voie avant la
     * validation. Absent des rapports calculés avant cette règle.
     */
    lignesInformatives: z.array(z.number().int().positive()).optional(),
    /**
     * Décision du TL (26/10) — les lignes qui ne semblent pas être des
     * personnes, avec leurs cellules : la chargée en réintègre une, ou tranche
     * une ligne `douteuse` (option A : la validation l'attend). Absent quand il
     * n'y en a pas, et des rapports calculés avant cette règle.
     */
    lignesNonPersonnes: z
        .array(z.object({
        numeroLigne: z.number().int().positive(),
        motif: z.enum([
            'ENTETE_ENTREPRISE',
            'LIGNE_INFORMATIVE',
            'PIED_DE_PAGE',
            'LIBELLE_DEUX_POINTS',
        ]),
        cellules: z.array(z.string()),
        douteuse: z.literal(true).optional(),
    }))
        .optional(),
    /**
     * Décision du TL (29/09) : le texte libre que l'entreprise écrit à côté
     * d'une personne, dans la colonne d'observations — salarié ou membre de sa
     * famille. MONTRÉ sur la ligne de la personne, jamais appliqué, jamais
     * journalisé. Absent quand le fichier n'en porte pas.
     */
    observations: z
        .array(z.object({
        numeroLigne: z.number().int(),
        nomComplet: z.string(),
        texte: z.string(),
    }))
        .optional(),
    /**
     * Décision 230 — import d'embauches et départs : les sortants retrouvés mais
     * déjà inactifs. Rien ne change pour eux ; absent quand il n'y en a pas.
     */
    dejaPartis: z
        .array(z.object({
        personneId: z.string().nullable(),
        numeroLigne: z.number().int().nullable(),
        nomComplet: z.string(),
        matricule: z.string().nullable(),
    }))
        .optional(),
    /**
     * Décision 272 — import d'embauches et départs : les sortants que rien ne
     * retrouve dans le contrat, tels que le fichier les écrit. Ils ne
     * s'appliquent pas ; le reste du fichier s'applique si la chargée le
     * confirme (`departsIntrouvablesConfirmes`). `departsDeclares` : tous les
     * sortants du fichier, pour le seuil de la moitié. Absent quand il n'y en a pas.
     */
    departsIntrouvables: z
        .object({
        departsDeclares: z.number().int().positive(),
        lignes: z.array(z.object({
            numeroLigne: z.number().int(),
            nomComplet: z.string(),
            matricule: z.string().nullable(),
        })),
    })
        .optional(),
    /**
     * Les feuilles lues, dans l'ordre, quand il y en a plusieurs : le numéro
     * d'une ligne s'y lit avec `numeroLisible`. Absent pour une feuille seule.
     */
    feuilles: z.array(z.string()).optional(),
    /** R7 (16/10) — les cellules « DATE INVALIDE » ou « - » du classeur, lues vides. */
    cellulesVidees: z.number().int().positive().optional(),
    /**
     * Décision 80 (16/10) — la colonne lue comme matricule ressemble à un numéro
     * de ligne (1 à n). `ignore` : elle n'a servi ni au rapprochement ni aux
     * fiches, comme proposé par défaut ; la chargée peut la garder.
     */
    matriculeNumeroDeLigne: z.object({ ignore: z.boolean() }).optional(),
    /**
     * Décision du TL (16/10, R3) — les mois plus anciens d'un classeur à
     * onglets mensuels : lus pour leurs salaires seulement, à leur période.
     * Absent sans série mensuelle.
     */
    periodesArchivees: z
        .array(z.object({
        feuille: z.string(),
        /** `AAAA-MM-JJ` ; nulle quand le dépôt n'a pas de période. */
        periode: z.string().nullable(),
        lignes: z.number().int().nonnegative(),
        /** Les lignes qui portent un salaire. */
        salaires: z.number().int().nonnegative(),
        /**
         * Les salaires qui seront écrits : ceux des personnes retrouvées (connues,
         * ou créées par la liste du dernier mois) ; 0 sans période. Absent d'un
         * rapport d'avant la revue S69.
         */
        gardes: z.number().int().nonnegative().optional(),
    }))
        .optional(),
    /**
     * N1 (S404) — les derniers mois d'une série mensuelle ne listent personne :
     * le dernier mois rempli (`actif`) fait la liste. Absent sinon.
     */
    moisVides: z.object({ actif: z.string(), vides: z.array(z.string()).min(1) }).optional(),
    /**
     * Aucun titulaire actif avant cet import : c'est la liste initiale. Il n'y
     * a pas de différences à lire, mais chaque valeur à vérifier.
     */
    premierImport: z.boolean().optional(),
    /** L'effectif que le pied du fichier annonce (« Agent : 12 »), s'il le fait. */
    effectifDeclare: z.number().int().nonnegative().nullable().optional(),
    /**
     * Les conjoints et enfants nommés sous leur salarié : qui sera créé, ce qui
     * complète un ayant droit connu, ce qui est écarté et pourquoi. Absent des
     * rapports calculés avant leur prise en compte.
     */
    ayantsDroit: z
        .object({
        nommes: z.number().int(),
        aCreer: z.array(z.object({
            numeroLigne: z.number().int(),
            nomComplet: z.string(),
            lien: z.enum(['CONJOINT', 'ENFANT']),
            dateNaissance: z.string().nullable(),
            titulaire: z.string(),
            /** Ce qui se corrige au premier import. Absent des rapports calculés avant. */
            nom: z.string().optional(),
            prenom: z.string().nullable().optional(),
            sexe: z.enum(['M', 'F']).nullable().optional(),
            /** CP-03 — écrit en rouge dans le fichier : créé inactif. */
            inactif: z.boolean().optional(),
            /** CP-03 — la sous-ligne est colorée : la chargée tranche. */
            couleur: schemaIndiceCouleur.optional(),
            /**
             * Réunion du 15/10 (famille, phase 1) — la même personne probable
             * (même nom, même date de naissance) a déjà une fiche, sous un autre
             * salarié ou salariée elle-même, ou une autre ligne de ce fichier la
             * crée déjà. Elle n'est pas créée d'office : la chargée tranche
             * (`doublonsDecides`). Absent quand rien ne ressemble.
             */
            existePeutEtre: z.array(schemaFicheSemblable).optional(),
        })),
        completes: z.number().int(),
        deja: z.number().int(),
        ecartes: z.array(z.object({ numeroLigne: z.number().int(), motif: z.string() })),
        /**
         * Décision 187 — les familles écartées parce que leur matricule n'est
         * celui d'aucun salarié du fichier ni de la base, regroupées par
         * matricule : à signaler à l'entreprise. Absent quand il n'y en a pas.
         */
        famillesSansSalarie: z
            .array(z.object({ matricule: z.string(), lignes: z.array(z.number().int()) }))
            .optional(),
        /**
         * Décision 188 — les lignes lues comme nouveaux salariés qui sont la même
         * personne probable qu'un conjoint ou un enfant déjà connu de la même entreprise.
         * `DEJA_CONNU` : son salarié est dans le fichier, rien n'est créé.
         * `A_DECIDER` : la chargée tranche (son salarié manque, la base et le
         * fichier se contredisent, ou plusieurs fiches répondent). Absent quand vide.
         */
        famillesConnues: z
            .array(z.object({
            numeroLigne: z.number().int(),
            /** `null` quand plusieurs fiches répondent. */
            personneId: z.string().nullable(),
            nomComplet: z.string(),
            lien: z.enum(['CONJOINT', 'ENFANT']),
            /** Le salarié de la fiche connue, en clair. */
            titulaire: z.string(),
            issue: z.enum(['DEJA_CONNU', 'A_DECIDER']),
            raison: z
                .enum(['SALARIE_ABSENT', 'MATRICULE_DIFFERENT', 'FONCTION', 'PLUSIEURS'])
                .nullable(),
        }))
            .optional(),
        /**
         * Décision du TL (16/10) — un salarié que ce fichier crée est peut-être
         * le conjoint ou l'enfant déjà connu ailleurs (même nom, même date de
         * naissance). Une mention, jamais une question : le salarié est créé ;
         * la chargée fusionne ensuite les deux fiches (écran « Doublons »).
         * Absent quand rien ne ressemble.
         */
        salariesPeutEtreConnus: z
            .array(z.object({
            numeroLigne: z.number().int(),
            existePeutEtre: z.array(schemaFicheSemblable),
        }))
            .optional(),
        /**
         * Les ayants droit déjà connus que le fichier nomme : ce qui change pour
         * chacun, ou rien. Absent des rapports calculés avant.
         */
        connus: z
            .array(z.object({
            numeroLigne: z.number().int(),
            nomComplet: z.string(),
            lien: z.enum(['CONJOINT', 'ENFANT']),
            titulaire: z.string(),
            /** Pour mener à la fiche ; absents des rapports calculés avant. */
            personneId: z.uuid().nullable().optional(),
            titulaireId: z.uuid().optional(),
            issue: z.enum(['COMPLETE', 'INCHANGE', 'DIFFERENT', 'REPETE']),
            /**
             * CP-03, décision du TL (29/09) : son nom est en rouge ou en
             * orange dans le fichier — il ne serait plus couvert. Proposé au
             * retrait, décoché d'abord comme un départ ; la chargée tranche.
             */
            parti: z.boolean().optional(),
            /**
             * Décision du TL (30/09) : seul son nom est coloré, pas toute sa
             * sous-ligne. Proposé au retrait quand même, « à vérifier ».
             */
            aVerifier: z.boolean().optional(),
            /**
             * Décisions du TL (16/10, 35 et 3) — déjà retiré, il est nommé de
             * nouveau : il reste inactif, sauf si la chargée le coche.
             */
            reapparu: z.boolean().optional(),
            /**
             * Revue S69 (A2, décision 55) — le motif de son retrait à la main,
             * montré avec le réapparu. Un décès ne se recouvre jamais par le DNS.
             */
            motifRetrait: z.enum(MOTIFS_RETRAIT_AYANT_DROIT).optional(),
            appris: z.object({
                dateNaissance: z.string().optional(),
                sexe: z.enum(['M', 'F']).optional(),
            }),
            differences: z.array(z.object({
                champ: z.enum(['DATE_NAISSANCE', 'SEXE', 'LIEN']),
                base: z.string(),
                fichier: z.string(),
            })),
        }))
            .optional(),
        /**
         * Connus en base sous un salarié du fichier, mais que le fichier ne
         * nomme plus. Rien ne leur arrive : c'est à vérifier, pas à appliquer.
         */
        absents: z
            .array(z.object({
            nomComplet: z.string(),
            lien: z.enum(['CONJOINT', 'ENFANT']),
            titulaire: z.string(),
            /** Pour mener à la fiche ; absents des rapports calculés avant. */
            personneId: z.uuid().optional(),
            titulaireId: z.uuid().optional(),
        }))
            .optional(),
    })
        .optional(),
});
/**
 * Une page du document papier (CP-11).
 *
 * Ni chemin ni empreinte ici : le client ouvre une page par son rang, et le
 * serveur seul sait où elle est archivée.
 */
export const schemaPageScan = z.object({
    ordre: z.number().int().positive(),
    type: z.string(),
    octets: z.number().int().nonnegative(),
    nomFichier: z.string().nullable(),
});
/** Combien de pages un DNS papier peut porter. Au-delà, c'est un classeur qu'il faut. */
export const PAGES_MAX_SCAN = 20;
export const schemaImportDns = z.object({
    id: z.uuid(),
    adherent: z.object({ id: z.uuid(), code: z.string(), nom: z.string() }),
    contratId: z.uuid().nullable(),
    support: z.enum(SUPPORTS_DNS),
    /** Décision 227 — liste complète, ou embauches et départs seulement. */
    mode: z.enum(MODES_IMPORT_DNS),
    /** Un fichier est archivé : le classeur, ou au moins une page de scan (CP-11). */
    aUnFichier: z.boolean(),
    /** Les pages du document papier, dans l'ordre. Vide pour un classeur déposé. */
    pages: z.array(schemaPageScan),
    /** Reçu sur papier ou en PDF, et la grille n'est pas terminée : il attend la chargée, pas le worker. */
    enAttenteDeSaisie: z.boolean(),
    statut: z.enum(STATUTS_IMPORT_DNS),
    nomFichier: z.string().nullable(),
    periodeDeclaree: z.string().nullable(),
    recuLe: z.string().nullable(),
    feuilleRetenue: z.string().nullable(),
    feuillesIgnorees: z.array(z.string()),
    /** Les feuilles cochées par la chargée ; vide quand le moteur a lu sa meilleure feuille seule. */
    feuillesChoisies: z.array(z.string()),
    volumes: z.object({
        lignesLues: z.number().int(),
        nouveaux: z.number().int(),
        modifies: z.number().int(),
        retires: z.number().int(),
        declaresPartis: z.number().int(),
        aConfirmer: z.number().int(),
        rejetees: z.number().int(),
    }),
    erreur: z.string().nullable(),
    creeLe: z.string(),
});
/**
 * Ce qu'une annulation a défait, et ce qu'elle a laissé : une valeur
 * modifiée à la main depuis l'application n'est pas écrasée.
 */
export const schemaBilanAnnulationDns = z.object({
    annuleLe: z.string(),
    personnesSupprimees: z.number().int(),
    valeursRestaurees: z.number().int(),
    /** Champs changés depuis l'application : gardés tels quels. */
    valeursConservees: z.number().int(),
    personnesReactivees: z.number().int(),
    salairesSupprimes: z.number().int(),
});
export const schemaDetailImportDns = schemaImportDns.extend({
    /** Le contrat que déclare l'import, en clair : « SMIE+ — cadres ». */
    contrat: z.string().nullable(),
    /** CP-03 — le seuil de désactivation de masse en vigueur, en %. */
    seuilDesactivationMasse: z.number(),
    /** Décision 184 — au-delà de `personnes`, ou de `pourcent` % des nouveaux si c'est moins, on bloque. */
    seuilsFamillesLues: z.object({ personnes: z.number(), pourcent: z.number() }),
    rapport: schemaRapportDiff.nullable(),
    /** Présent sur un import annulé. */
    annulation: schemaBilanAnnulationDns.nullable(),
    /**
     * Ce que la chargée a décidé à la validation — les corrections tapées et
     * les lignes exclues au premier import. `null` tant que rien n'est validé.
     */
    decisions: z
        .object({
        corrections: z.array(z.object({ numeroLigne: z.number().int(), champ: z.string(), valeur: z.string() })),
        lignesExclues: z.array(z.number().int()),
        /** CP-03 — ce que la chargée a tranché, parti ou actif, pour chaque ligne colorée. */
        couleursDecidees: z.array(z.object({ numeroLigne: z.number().int(), parti: z.boolean() })),
        /** Réunion du 15/10 — même personne ou non, pour chaque ayant droit qui existe peut-être. */
        doublonsDecides: z
            .array(z.object({ numeroLigne: z.number().int(), memePersonne: z.boolean() }))
            .optional(),
        /**
         * Combien de retraits et de départs elle a acceptés : pendant
         * l'application, l'écran dit combien de personnes vont perdre
         * l'éligibilité.
         */
        retiresAcceptes: z.number().int(),
        partisAcceptes: z.number().int(),
        /** Les feuilles dont rien ne s'applique (décision du TL, 29/09). */
        feuillesEcartees: z.array(z.string()),
        /** Combien de conjoints et d'enfants au nom rouge ou orange elle a retirés. */
        ayantsDroitRetires: z.number().int(),
    })
        .nullable(),
    /**
     * Les autres imports du même classeur : réparti feuille par feuille sur
     * plusieurs contrats, chacun a le sien. Vide pour un classeur lu d'un bloc.
     */
    importsLies: z.array(z.object({
        id: z.uuid(),
        statut: z.enum(STATUTS_IMPORT_DNS),
        contrat: z.string(),
        feuilles: z.array(z.string()),
    })),
    /**
     * Qui l'application a créé, pour mener à sa fiche depuis le rapport : les
     * salariés par ligne du fichier, les ayants droit par leur rang dans
     * `rapport.ayantsDroit.aCreer`. `null` tant que l'import n'est pas
     * appliqué, après une annulation, ou pour un import appliqué avant.
     */
    creees: z
        .object({
        titulaires: z.array(z.object({ numeroLigne: z.number().int(), personneId: z.string() })),
        ayantsDroit: z.array(z.object({ rang: z.number().int(), personneId: z.string() })),
    })
        .nullable(),
    /** L'instant de l'application ; `null` tant que l'import n'est pas appliqué. */
    appliqueLe: z.string().nullable(),
    /** Ce que l'application a réellement écrit ; `null` avant l'application. */
    effets: z
        .object({
        personnesCreees: z.number().int(),
        fichesModifiees: z.number().int(),
        personnesDesactivees: z.number().int(),
    })
        .nullable(),
});
export const schemaListeImportsDns = z.object({ imports: z.array(schemaImportDns) });
/**
 * Ce qui attend la chargée, rangé par ce qu'elle a à faire. À la mise en
 * service, les premiers DNS de plusieurs centaines d'entreprises attendent en
 * même temps : sans ce rangement, elle ne voyait que les cent plus anciens.
 */
export const ETAPES_A_TRAITER = ['RAPPORT', 'FEUILLES', 'COLONNES', 'SAISIE', 'ECHEC'];
/** Une page de la liste « à traiter ». */
export const TAILLE_PAGE_A_TRAITER = 100;
export const schemaFiltreATraiter = z
    .object({
    etape: z.enum(ETAPES_A_TRAITER).optional(),
    page: z.coerce.number().int().min(0).max(1_000).default(0),
})
    .strict();
export const schemaListeATraiter = z.object({
    imports: z.array(schemaImportDns),
    /** Combien attendent à chaque étape, toutes pages confondues. */
    compteurs: z.object({
        RAPPORT: z.number().int(),
        FEUILLES: z.number().int(),
        COLONNES: z.number().int(),
        SAISIE: z.number().int(),
        ECHEC: z.number().int(),
    }),
    /** Le nombre d'imports que le filtre retient, toutes pages confondues. */
    total: z.number().int(),
    /** Les rapports sans rien à décider, qui se valident d'un geste (500 au plus). */
    sansChangement: z.number().int(),
    /**
     * Les imports que la personne connectée a validés et que le serveur n'a pas
     * fini d'appliquer : l'écran suit une validation groupée jusqu'au bout.
     */
    enApplication: z.number().int(),
});
/** Sans `parmi`, tous les rapports sans changement ; avec, ceux-là seulement. */
export const schemaValidationGroupee = z
    .object({ parmi: z.array(z.uuid()).max(500).optional() })
    .strict();
export const schemaBilanValidationGroupee = z.object({
    valides: z.number().int(),
    /** Recalculés ou refusés entre-temps : ils restent à ouvrir un par un. */
    laisses: z.number().int(),
});
export const schemaFiltreImportsDns = z.object({ adherentId: z.uuid() }).strict();
export const schemaValidationImportDns = z
    .object({
    /** Retirés (absents du DNS) dont la désactivation est acceptée. */
    retiresAcceptes: z.array(z.uuid()).max(20_000),
    /** Déclarés partis dont la désactivation est acceptée. */
    partisAcceptes: z.array(z.uuid()).max(20_000),
    /**
     * Les champs dont la chargée refuse la correction, un par un.
     *
     * Par défaut une modification s'applique : c'est le cas courant, un DNS
     * qui complète ce qu'on ne savait pas. Mais un fichier peut porter une
     * date de naissance fausse, et sans ce refus elle écraserait la bonne —
     * la chargée ne pouvait que la corriger après coup.
     *
     * Le refus est au CHAMP, pas à la ligne : on refuse une date de naissance
     * douteuse sans refuser le changement de fonction de la même personne.
     */
    modificationsRefusees: z
        .array(z.object({ personneId: z.uuid(), champ: z.string().max(60) }).strict())
        .max(20_000)
        .default([]),
    /** Réunion du 15/10 — les réapparus que la chargée garde inactifs (décochés). */
    reactivationsRefusees: z.array(z.uuid()).max(20_000).default([]),
    /** Décision 3 du TL (16/10) — les réapparus partis à la main que la chargée réactive (cochés). */
    reactivationsAcceptees: z.array(z.uuid()).max(20_000).default([]),
    /**
     * Premier import : les valeurs corrigées à l'écran avant la création,
     * une par cellule. Lues avec les règles du fichier (`lireCorrection`).
     */
    corrections: z
        .array(z
        .object({
        numeroLigne: z.number().int().positive(),
        champ: z.enum(CHAMPS_CORRIGEABLES),
        valeur: z.string().max(200),
    })
        .strict())
        .max(20_000)
        .default([]),
    /** Les nouveaux que la chargée ne veut pas créer, par numéro de ligne. */
    lignesExclues: z.array(z.number().int().positive()).max(20_000).default([]),
    /**
     * CP-03 — pour chaque ligne colorée d'une personne à créer (salarié,
     * conjoint ou enfant) : la chargée dit « parti » ou « actif ». La couleur
     * propose, elle ne décide pas ; le serveur refuse tant qu'une ligne
     * colorée n'est pas tranchée. Depuis le 30/09, la ligne entière colorée
     * n'a plus à l'être : elle est créée partie, sauf « actif » dit ici.
     */
    couleursDecidees: z
        .array(z.object({ numeroLigne: z.number().int().positive(), parti: z.boolean() }).strict())
        .max(20_000)
        .default([]),
    /**
     * Réunion du 15/10 (famille, phase 1) — pour chaque conjoint ou enfant à
     * créer qui existe peut-être déjà (`existePeutEtre`) : « même personne »
     * (rien n'est créé) ou non (créé quand même). Jamais automatique : le
     * serveur refuse tant qu'une ligne n'est pas tranchée.
     */
    doublonsDecides: z
        .array(z.object({ numeroLigne: z.number().int().positive(), memePersonne: z.boolean() }).strict())
        .max(20_000)
        .default([]),
    /**
     * Décision du TL (29/09) : les feuilles d'un même contrat forment un seul
     * rapport, et la chargée dit, feuille par feuille, si elle s'applique.
     * Une feuille écartée n'applique rien de ses lignes — ni création, ni
     * modification, ni départ — et ceux qu'elle nomme ne sont pas retirés.
     * Par nom de feuille, tel que le rapport les liste.
     */
    feuillesEcartees: z.array(z.string().max(200)).max(200).default([]),
    /**
     * CP-03, décision du TL (29/09) — les conjoints et enfants connus dont le
     * nom est en rouge ou en orange, et que la chargée retire : ils passent
     * Inactif. Ceux qu'elle ne coche pas restent couverts.
     */
    ayantsDroitRetires: z.array(z.uuid()).max(20_000).default([]),
    /**
     * Décision du TL (30/09) — les déclarés partis que la chargée garde
     * actifs. Un départ par la ligne entière colorée s'applique sans elle,
     * sauf s'il est ici ; un départ au nom ou au matricule seul coloré doit
     * être ici ou dans `partisAcceptes`.
     */
    partisGardes: z.array(z.uuid()).max(20_000).default([]),
    /** De même, les conjoints et enfants connus colorés que la chargée garde couverts. */
    ayantsDroitGardes: z.array(z.uuid()).max(20_000).default([]),
    /** Décision du TL (16/10) — les conjoints et enfants réapparus qu'elle recouvre. */
    ayantsDroitReactives: z.array(z.uuid()).max(20_000).default([]),
    /** Premier import : la chargée atteste avoir regardé les lignes signalées. */
    signalementsVerifies: z.boolean().default(false),
    /**
     * Décision 272 — des départs du fichier ne sont pas dans le contrat : la
     * chargée confirme que c'est bien le fichier de ce contrat, et le reste
     * s'applique sans eux. Jamais par défaut : sans elle, le serveur refuse.
     */
    departsIntrouvablesConfirmes: z.boolean().default(false),
    /** Exigé au-delà de 30 % de l'effectif désactivé (CP-03). */
    motifDesactivation: z.string().max(1000).optional(),
    /** Décision 184 — exigé quand trop de nouveaux salariés sont peut-être des ayants droit connus. */
    motifFamillesLues: z.string().max(1000).optional(),
})
    .strict();
/**
 * Décision 272 — plus de la moitié des départs du fichier ne sont pas dans le
 * contrat : ce n'est probablement pas le fichier de ce contrat.
 */
export function fichierProbablementAutre(d) {
    return d.lignes.length * 2 > d.departsDeclares;
}
export const schemaRejetImportDns = z.object({ motif: z.string().trim().min(3).max(1000) }).strict();
/** Défaire un import appliqué : le même motif obligatoire qu'un rejet. */
export const schemaAnnulationImportDns = schemaRejetImportDns;
// ── Outil de mapping de colonnes (Écran 4) ──────────────────────────────────
/** Les champs tels que la chargée les lit, dans l'ordre du gabarit. */
export const LIBELLE_CHAMP_DNS = {
    matricule: 'Matricule',
    nom: 'Nom (et prénoms)',
    prenom: 'Prénoms',
    fonction: 'Fonction',
    estAgent: 'Case « agent » (A)',
    nbConjoint: 'Conjoint (C)',
    nbEnfant: 'Enfants (E)',
    dateNaissance: 'Date de naissance',
    sexe: 'Sexe',
    lieuAffectation: 'Lieu d’affectation',
    dateEntree: 'Date d’entrée',
    dateDepart: 'Date de départ',
    salaireBrut: 'Salaire brut',
    nomAyantDroit: 'Nom du conjoint ou de l’enfant',
    prenomAyantDroit: 'Prénoms du conjoint ou de l’enfant',
};
/** Au-delà, l'aperçu n'aide plus : la chargée a le fichier ouvert à côté. */
export const LIGNES_APERCU_MAPPING = 40;
export const COLONNES_MAX_MAPPING = 60;
// Zod 4 : un `record` à clés énumérées exige toutes les clés ; un mapping n'en
// porte que certaines, d'où `partialRecord`.
const colonnesMapping = z
    .partialRecord(z.enum(CHAMPS_RESOLUBLES), z.number().int().min(0).max(255))
    .refine((c) => c.nom !== undefined, 'La colonne du nom est obligatoire : sans elle, aucune ligne ne se lit.')
    .refine((c) => new Set(Object.values(c)).size === Object.values(c).length, 'Une colonne ne peut porter qu’un seul champ.');
export const schemaMappingDns = z
    .object({
    feuille: z.string().min(1).max(255),
    /** Numéro de la première ligne de données, tel que le tableur l'affiche. */
    premiereLigne: z.number().int().min(1).max(20_000),
    colonnes: colonnesMapping,
    /** Rejouer ce mapping aux prochains DNS de l'entreprise. */
    memoriser: z.boolean().default(true),
})
    .strict();
/** Décision 80 — garder ou ignorer la colonne du matricule qui ressemble à un numéro de ligne. */
export const schemaColonneMatriculeDns = z.object({ garder: z.boolean() }).strict();
/**
 * Décision du TL (26/10) — la chargée dit si ces lignes sont des personnes.
 * L'analyse repart : le rapport n'est plus le même.
 */
export const schemaLignesPersonnesDns = z
    .object({
    lignes: z.array(z.number().int().positive()).min(1).max(1000),
    personne: z.boolean(),
})
    .strict();
export const ORIGINES_MAPPING = ['AUTOMATIQUE', 'MEMORISE', 'IMPOSE'];
export const schemaFiltreApercuMapping = z
    .object({ feuille: z.string().max(255).optional() })
    .strict();
export const schemaApercuMapping = z.object({
    import: schemaImportDns,
    feuilles: z.array(z.object({ nom: z.string(), nbLignes: z.number().int() })),
    /** La feuille montrée : celle demandée, sinon celle du mapping en vigueur, sinon la mieux reconnue. */
    feuille: z.string(),
    /** Les premières lignes de la feuille, en texte. */
    lignes: z.array(z.array(z.string())),
    /** Ce que l'écran pré-remplit, et d'où cela vient. */
    proposition: z.object({
        origine: z.enum(ORIGINES_MAPPING),
        premiereLigne: z.number().int(),
        colonnes: z.record(z.string(), z.number().int()),
    }),
    /** L'entreprise a déjà un mapping mémorisé. */
    memorise: z.boolean(),
});
// ── Classeur à plusieurs feuilles ───────────────────────────────────────────
const schemaFeuilleContrat = z.object({
    feuille: z.string().min(1).max(255),
    contratId: z.uuid(),
});
/** Ce que l'écran des feuilles montre : de quoi reconnaître chaque feuille, et les contrats. */
export const schemaFeuillesDns = z.object({
    import: schemaImportDns,
    feuilles: z.array(z.object({
        nom: z.string(),
        lignes: z.number().int(),
        entete: z.array(z.string()),
        /** Sans colonne du nom reconnue : proposée décochée. */
        nomReconnu: z.boolean(),
    })),
    /** La feuille que le moteur lirait seul : cochée d'office plutôt que la première. */
    retenue: z.string().nullable(),
    contrats: z.array(z.object({ id: z.uuid(), libelle: z.string() })),
    /** Le choix du DNS précédent, quand le classeur a les mêmes feuilles ou la même forme. */
    memorise: z.array(schemaFeuilleContrat).nullable(),
});
/** Les feuilles cochées et le contrat de chacune. Un contrat = un import. */
export const schemaRepartitionFeuilles = z
    .object({
    repartition: z
        .array(schemaFeuilleContrat)
        .min(1, 'Cochez au moins une feuille.')
        .max(100)
        .refine((r) => new Set(r.map((f) => f.feuille)).size === r.length, 'Une feuille ne se lit qu’une fois.'),
})
    .strict();
// ── Saisie de masse (CP-11) ─────────────────────────────────────────────────
/** Une entreprise de 2 000 salariés ne déclare pas sur papier ; au-delà, c'est une erreur. */
export const LIGNES_MAX_SAISIE = 2_000;
const celluleSaisie = z.string().max(200);
export const schemaLigneSaisie = z
    .object({
    matricule: celluleSaisie,
    nom: celluleSaisie,
    prenom: celluleSaisie,
    fonction: celluleSaisie,
    nbConjoint: celluleSaisie,
    nbEnfant: celluleSaisie,
    dateNaissance: celluleSaisie,
    sexe: celluleSaisie,
    lieuAffectation: celluleSaisie,
    dateEntree: celluleSaisie,
    dateDepart: celluleSaisie,
    /**
     * Facultatif dans le schéma : absent pour qui n'a pas `salaire:read`,
     * et d'un brouillon enregistré avant l'ajout de la colonne.
     */
    salaireBrut: celluleSaisie.optional(),
    parti: z.boolean(),
    aVerifier: z.array(z.enum(CHAMPS_SAISIE)).max(CHAMPS_SAISIE.length),
    confirmeParRegistre: z.boolean(),
})
    .strict();
export const schemaSaisieDns = z
    .object({ lignes: z.array(schemaLigneSaisie).max(LIGNES_MAX_SAISIE) })
    .strict();
/** Où en est la lecture automatique du scan ou du PDF (elle pré-remplit, elle ne bloque jamais). */
export const STATUTS_LECTURE_DNS = ['SANS_OBJET', 'EN_ATTENTE', 'FAITE', 'INDISPONIBLE'];
export const schemaEtatSaisie = z.object({
    import: schemaImportDns,
    lignes: z.array(schemaLigneSaisie),
    /**
     * La stratégie en vigueur (paramètre `dns.scan_avant_saisie`) : vrai, la
     * grille ne s'ouvre qu'une fois la première page rattachée ; faux, « saisir
     * seulement ». Les pages, elles, se lisent dans `import.pages`.
     */
    scanExige: z.boolean(),
    lecture: z.object({
        statut: z.enum(STATUTS_LECTURE_DNS),
        /** Ce que la chargée doit savoir : « 18 lignes lues », « lecture impossible, saisissez »… */
        message: z.string().nullable(),
        /**
         * La lecture a reconstitué un tableau sans savoir l'interpréter : la
         * chargée peut y désigner les colonnes elle-même. Le tableau lui-même se
         * demande à part — il pèse trop pour voyager à chaque relecture de l'état.
         */
        aUnTableau: z.boolean(),
    }),
});
/**
 * Le tableau que la lecture a reconstitué, et ce qu'elle propose d'en faire.
 *
 * Même idée que l'aperçu de l'Écran 4, sur une image au lieu d'un classeur :
 * des lignes de texte, et un mapping à confirmer ou à corriger.
 */
export const schemaTableauLu = z.object({
    lignes: z.array(z.array(z.string())),
    proposition: z.object({
        premiereLigne: z.number().int().positive(),
        colonnes: z.record(z.string(), z.number().int()),
    }),
});
/**
 * Ce que la chargée désigne sur le tableau lu. Une seule « feuille » : le
 * document. Les mêmes garde-fous que l'Écran 4 — le nom est obligatoire, deux
 * champs ne partagent pas une colonne.
 */
export const schemaMappingLecture = z
    .object({
    premiereLigne: z.number().int().min(1).max(20_000),
    colonnes: colonnesMapping,
})
    .strict();
//# sourceMappingURL=dns.js.map